import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import {
  Box,
  CardShipment,
  FiservCard,
  FiservCardInterface,
  FiservCardStatusEnum,
  getUtcDate,
  Preloader,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import styled, { css } from 'styled-components';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE, Roles } from '@/common/constants';
import ROUTES from '@/common/routes';
import { useHasAccess } from '@/modules/core/hooks';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import { useSearchParams } from '@/modules/transaction/hooks/useSearchParams';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { useAuthStore } from '@/modules/user/stores';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import { useCardShipmentTrackerQuery } from '../../queries';
import { CardActionsCarousel } from '../CardActionsCarousel/CardActionsCarousel';

const isNationwideTheme = whiteLabelingConfig.key === THEMES.nationwide;

const Wrapper = styled(Box)`
  ${({ theme }) => {
    if (isNationwideTheme) {
      return css`
        .card-shipment_footer_item_label,
        .card-shipment_item_item_date_month,
        .card-shipment_item_item_title {
          color: ${theme.colors.textOnColor} !important;
        }
      `;
    }
    return css``;
  }}
`;

const NewCardCarousel: React.FC = () => {
  const { push } = useHistory();
  const { query: cardId } = useSearchParams('card_id');
  const { query: dependentId } = useSearchParams('dependent_id');
  const { user } = useAuthStore();
  const { organizationProperties } = useOrganizationProperties();
  const { isMobile } = useResponsive();
  const isEmployeeLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const { cardMap, isLoading, holderKey } = useGetCardsQuery(user?.employee?.id, dependentId);

  const cards: FiservCardInterface[] = useMemo(
    () =>
      Object.keys(cardMap[holderKey] || {}).reduce(
        (arr, status) => [
          ...arr,
          ...(cardMap[holderKey][status as FiservCardStatusEnum] || ([] as FiservCardInterface[])),
        ],
        [] as FiservCardInterface[]
      ),
    [holderKey, cardMap]
  );
  const selectedItemIndex = useMemo(() => {
    const index = cards.findIndex((card) => card.id === cardId);
    return index > -1 ? index : 0;
  }, [cardId, cards]);

  const currentCard = useMemo(() => cards?.[selectedItemIndex], [cards, selectedItemIndex]);
  const { isLoading: isTrackerLoading, data: shipmentList } = useCardShipmentTrackerQuery({
    id: currentCard?.id,
  });
  const getInfoDate = useCallback(
    (date: Date | null) => (date ? getUtcDate(date).format(DEFAULT_DATE_FORMAT) : EMPTY_FIELD_VALUE),
    []
  );

  if (isLoading) {
    return (
      <Box background="module" pad="spacing24" round="moduleRound">
        <Preloader />
      </Box>
    );
  }

  return (
    <Wrapper background="module" round="moduleRound">
      {currentCard ? (
        <CardShipment
          isMobileMode={isMobile}
          footer={
            <CardShipment.Footer>
              <CardShipment.Footer.Item id="1" label="Card Request Date">
                {getInfoDate(currentCard.cardRequestDate)}
              </CardShipment.Footer.Item>
              <CardShipment.Footer.Item id="2" label="Mailed Date">
                {getInfoDate(currentCard.mailedDate)}
              </CardShipment.Footer.Item>
              <CardShipment.Footer.Item id="3" label="Activation Date">
                {getInfoDate(currentCard.activationDate)}
              </CardShipment.Footer.Item>
              <CardShipment.Footer.Item id="4" isLastIndex label="Expiration Date">
                {getInfoDate(currentCard.expDate)}
              </CardShipment.Footer.Item>
            </CardShipment.Footer>
          }
          right={isTrackerLoading ? <Preloader /> : <CardShipment.List list={shipmentList} />}
        >
          <Box>
            <FiservCard
              key={currentCard.id}
              card={currentCard}
              isFullyMasked={isEmployeeLimitedAccess}
              logos={{
                logoUrl: organizationProperties?.darkLargeLogoUrl,
                cardLogo: whiteLabelingConfig?.images?.cardLogo,
                defaultCardLogo: whiteLabelingConfig?.logo,
                cardBackground: whiteLabelingConfig?.images?.cardBackground,
              }}
              onPushWithParams={() => {
                push(
                  `${ROUTES.CARD_DETAILS}?dependent_id=${currentCard.holder.dependentId}&card_id=${currentCard.id}`
                );
              }}
              isLoading={isLoading}
            />

            <Box fill direction="row" margin={{ top: 'spacing12' }}>
              <CardActionsCarousel isDarkMode cards={cards} selectedItemIndex={selectedItemIndex} />
            </Box>
          </Box>
          <Box></Box>
        </CardShipment>
      ) : (
        <Box justify="center" align="center">
          <Text color="textSecondary" size="14px" weight={400} margin={{ vertical: 's' }}>
            The employee doesn't have any cards yet.
          </Text>
        </Box>
      )}
    </Wrapper>
  );
};

export default NewCardCarousel;
